import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { productService, productCategories, stoneTypes, type Product } from '../../../services';

const ProductCreatePage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  
  // Image Upload State
  const [files, setFiles] = useState<FileList | null>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    sku: '',
    description: '',
    category: '',
    karat: 18,
    gold_color: 'yellow',
    net_weight: 0,
    stock: 0,
    labor_fee_percent: 0,
    labor_fee_fixed: '0',
    profit_percent: 0,
    tax_percent: 0,
    is_active: true,
    is_featured: false,
    stones: [],
  });

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(e.target.files);
      
      // Generate previews
      const newPreviews: string[] = [];
      Array.from(e.target.files).forEach(file => {
        newPreviews.push(URL.createObjectURL(file));
      });
      setPreviewUrls(newPreviews);
    }
  };

  const submit = async () => {
    if (!formData.name || !formData.category) return;
    setSaving(true);
    try {
      // 1. Create Product
      const newProduct = await productService.createProduct(formData);

      // 2. Upload Images if selected
      if (files && files.length > 0 && newProduct.id) {
        await productService.uploadImages(newProduct.id, files);
      }

      navigate('/admin/products', { replace: true });
    } catch (error) {
      console.error('Error creating product:', error);
      alert('خطا در ایجاد محصول');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ایجاد محصول جدید</h1>
          <p className="text-gray-500 mt-1">اطلاعات محصول را وارد کنید و ذخیره نمایید</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            className="px-6 py-2.5 rounded-xl text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 font-medium transition-colors" 
            onClick={() => navigate('/admin/products')}
          >
            انصراف
          </button>
          <button 
            className="px-6 py-2.5 rounded-xl bg-[#0e444e] text-white hover:bg-[#0b363e] shadow-lg shadow-[#0e444e]/20 font-medium transition-all disabled:opacity-50 disabled:shadow-none" 
            onClick={submit} 
            disabled={saving || !(formData.name && formData.category)}
          >
            {saving ? 'در حال ایجاد...' : 'ایجاد محصول'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content (Left) */}
        <div className="lg:col-span-2 space-y-8">
          
          {/* Images Section */}
          <div className="card overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">تصاویر محصول</h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-md">
                {previewUrls.length} تصویر انتخاب شده
              </span>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
              {previewUrls.map((url, index) => (
                <div key={`new-${index}`} className="relative aspect-square rounded-xl overflow-hidden border-2 border-[--color-accent] border-dashed">
                  <img src={url} alt="New Upload" className="w-full h-full object-cover" />
                  <div className="absolute top-1 right-1 bg-[--color-accent] text-[--color-primary] text-[10px] font-bold px-1.5 py-0.5 rounded">جدید</div>
                </div>
              ))}
            </div>

            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-[--color-accent] hover:bg-[--color-accent]/5 transition-all group"
            >
              <input 
                type="file" 
                multiple 
                accept="image/*" 
                className="hidden" 
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3 group-hover:bg-white group-hover:shadow-md transition-all">
                <svg className="w-6 h-6 text-gray-400 group-hover:text-[--color-accent]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-700">برای آپلود کلیک کنید</p>
              <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
            </div>
          </div>

          {/* Basic Info */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-6 border-b pb-4">اطلاعات پایه</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="input-label">نام محصول</label>
                <input 
                  className="input-field" 
                  placeholder="مثال: انگشتر طلا طرح قلب" 
                  value={formData.name || ''} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                />
              </div>
              <div>
                <label className="input-label">کد محصول (SKU)</label>
                <input 
                  className="input-field font-mono text-left" 
                  placeholder="R-1001" 
                  dir="ltr"
                  value={formData.sku || ''} 
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })} 
                />
              </div>
              <div className="md:col-span-2">
                <label className="input-label">توضیحات</label>
                <textarea 
                  className="input-field min-h-[120px] resize-y" 
                  placeholder="توضیحات کامل محصول..." 
                  value={formData.description || ''} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                />
              </div>
            </div>
          </div>

          {/* Stone Details */}
          <div className="card">
            <div className="flex items-center justify-between mb-6 border-b pb-4">
              <h3 className="text-lg font-bold text-gray-900">سنگ‌های قیمتی</h3>
              <button
                className="text-sm text-[--color-accent] hover:text-[--color-accent-dark] font-medium flex items-center gap-1"
                onClick={() => setFormData({
                  ...formData,
                  stones: [...(formData.stones || []), { type: 'none', count: 1, weight: 0, price: '0', is_precious: false }]
                })}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                افزودن سنگ
              </button>
            </div>
            
            <div className="space-y-4">
              {(!formData.stones || formData.stones.length === 0) && (
                <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                  <p className="text-gray-400 text-sm">هیچ سنگی اضافه نشده است</p>
                </div>
              )}
              
              {formData.stones?.map((stone, index) => (
                <div key={index} className="p-4 rounded-xl bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors relative group">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="md:col-span-1">
                      <label className="text-xs text-gray-500 mb-1.5 block">نوع سنگ</label>
                      <select
                        className="input-field py-2 text-sm"
                        value={stone.type}
                        onChange={(e) => {
                          const newStones = [...(formData.stones || [])];
                          newStones[index] = { ...stone, type: e.target.value };
                          setFormData({ ...formData, stones: newStones });
                        }}
                      >
                        {stoneTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1.5 block">تعداد</label>
                      <input
                        type="number"
                        className="input-field py-2 text-sm"
                        value={stone.count}
                        onChange={(e) => {
                          const newStones = [...(formData.stones || [])];
                          newStones[index] = { ...stone, count: Number(e.target.value) };
                          setFormData({ ...formData, stones: newStones });
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1.5 block">وزن (قیراط)</label>
                      <input
                        type="number"
                        className="input-field py-2 text-sm"
                        value={stone.weight}
                        onChange={(e) => {
                          const newStones = [...(formData.stones || [])];
                          newStones[index] = { ...stone, weight: Number(e.target.value) };
                          setFormData({ ...formData, stones: newStones });
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1.5 block">قیمت (تومان)</label>
                      <input
                        type="text"
                        className="input-field py-2 text-sm"
                        value={stone.price}
                        onChange={(e) => {
                          const newStones = [...(formData.stones || [])];
                          newStones[index] = { ...stone, price: e.target.value };
                          setFormData({ ...formData, stones: newStones });
                        }}
                      />
                    </div>
                    <div className="flex items-end pb-2 gap-3">
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-[--color-accent] rounded border-gray-300 focus:ring-[--color-accent]"
                          checked={stone.is_precious}
                          onChange={(e) => {
                            const newStones = [...(formData.stones || [])];
                            newStones[index] = { ...stone, is_precious: e.target.checked };
                            setFormData({ ...formData, stones: newStones });
                          }}
                        />
                        <span className="text-sm text-gray-600">قیمتی</span>
                      </label>
                      <button
                        className="text-red-400 hover:text-red-600 p-1 rounded-md hover:bg-red-50 transition-colors mr-auto"
                        title="حذف سنگ"
                        onClick={() => {
                          const newStones = [...(formData.stones || [])];
                          newStones.splice(index, 1);
                          setFormData({ ...formData, stones: newStones });
                        }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar (Right) */}
        <div className="space-y-6">
          
          {/* Status & Visibility */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">وضعیت انتشار</h3>
            <div className="space-y-4">
              <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <span className="text-sm font-medium text-gray-700">وضعیت فعال</span>
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-[--color-primary] rounded border-gray-300 focus:ring-[--color-primary]"
                  checked={!!formData.is_active} 
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} 
                />
              </label>
              <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer hover:bg-gray-100 transition-colors">
                <span className="text-sm font-medium text-gray-700">نمایش در ویژه</span>
                <input 
                  type="checkbox" 
                  className="w-5 h-5 text-[--color-accent] rounded border-gray-300 focus:ring-[--color-accent]"
                  checked={!!formData.is_featured} 
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} 
                />
              </label>
            </div>
          </div>

          {/* Categorization */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">دسته‌بندی</h3>
            <div className="space-y-4">
              <div>
                <label className="input-label">دسته محصول</label>
                <select 
                  className="input-field" 
                  value={formData.category || ''} 
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">انتخاب کنید...</option>
                  {productCategories.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                </select>
              </div>
              <div>
                <label className="input-label">موجودی انبار</label>
                <input 
                  type="number" 
                  className="input-field" 
                  value={formData.stock || 0} 
                  onChange={(e) => setFormData({ ...formData, stock: Number(e.target.value) })} 
                />
              </div>
            </div>
          </div>

          {/* Pricing & Weight */}
          <div className="card">
            <h3 className="text-lg font-bold text-gray-900 mb-4">قیمت و وزن</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">عیار طلا</label>
                  <select 
                    className="input-field" 
                    value={String(formData.karat || 18)} 
                    onChange={(e) => setFormData({ ...formData, karat: Number(e.target.value) })}
                  >
                    <option value={18}>18 عیار</option>
                    <option value={21}>21 عیار</option>
                    <option value={24}>24 عیار</option>
                  </select>
                </div>
                <div>
                  <label className="input-label">رنگ طلا</label>
                  <select 
                    className="input-field" 
                    value={formData.gold_color || 'yellow'} 
                    onChange={(e) => setFormData({ ...formData, gold_color: e.target.value })}
                  >
                    <option value="yellow">زرد</option>
                    <option value="white">سفید</option>
                    <option value="rose">رزگلد</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="input-label">وزن خالص (گرم)</label>
                <input 
                  type="number" 
                  step="0.01"
                  className="input-field" 
                  value={formData.net_weight || 0} 
                  onChange={(e) => setFormData({ ...formData, net_weight: Number(e.target.value) })} 
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">درصد اجرت</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    value={formData.labor_fee_percent || 0} 
                    onChange={(e) => setFormData({ ...formData, labor_fee_percent: Number(e.target.value) })} 
                  />
                </div>
                <div>
                  <label className="input-label">اجرت ثابت</label>
                  <input 
                    type="text" 
                    className="input-field" 
                    value={formData.labor_fee_fixed || '0'} 
                    onChange={(e) => setFormData({ ...formData, labor_fee_fixed: e.target.value })} 
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">درصد سود</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    value={formData.profit_percent || 0} 
                    onChange={(e) => setFormData({ ...formData, profit_percent: Number(e.target.value) })} 
                  />
                </div>
                <div>
                  <label className="input-label">درصد مالیات</label>
                  <input 
                    type="number" 
                    className="input-field" 
                    value={formData.tax_percent || 0} 
                    onChange={(e) => setFormData({ ...formData, tax_percent: Number(e.target.value) })} 
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ProductCreatePage;
